import * as _ from 'underscore'

import { ColumnModel, FieldModel } from "../api/api.models"

import { Enums } from "../../models/common"
import { OpConsumerType } from "../../models/core/op-consumer-type"
import { IOpData, IOpObject, IReferenceType } from "../../models/util/interfaces"
import { OpData, OpDataType } from "../../models/core"
import { OpDataList } from "../../models/util"

import { IUserSession, LocalizationHelper, ToastHelper, ConvertHelper, LogHelper } from "../../helpers"

import { DetailsOptions } from "../common/details-popup/details-popup.directive"
import { CreateEditInitParams } from "../common/create-edit-popup/create-edit-popup.controller"

export class ConsumerTypeHelper {

    // #region Consts

    private static get NAME_DV_KEY(): string { return 'Name' }
    private static get ID_DISTRIBUTOR_DV_KEY(): string { return 'IdDistributor' }
    private static get CONSUMER_COUNT_DV_KEY(): string { return 'ConsumerCount' }

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.ConsumerType]' }
    public static get FILE_NAME_PREFIX(): string { return 'consumer-types' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdConsumerType }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Consumer_Type }

    // #endregion


    // #region Public Methods

    public static createOpConsumerTypeFromIOpObject(sourceIOpObject: IOpObject, sourceConsumerType: OpConsumerType = null): OpConsumerType {
        if (sourceIOpObject == null) {
            return null
        }

        const consumerType: OpConsumerType = new OpConsumerType(null)

        consumerType.idConsumerType = sourceIOpObject.idObject
        consumerType.opState = sourceIOpObject.opState

        const nameData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_NAME)
        const distributorData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_ID_DISTRIBUTOR)

        if (nameData != null) {
            consumerType.dynamicValues.push({
                Key: ConsumerTypeHelper.NAME_DV_KEY,
                Value: nameData.value
            })
        }

        if (distributorData != null) {
            consumerType.dynamicValues.push({
                Key: ConsumerTypeHelper.ID_DISTRIBUTOR_DV_KEY,
                Value: distributorData.value
            })
        }

        return consumerType
    }

    public static getConsumerType(userSession: IUserSession, idConsumerType: number): JQueryPromise<OpConsumerType> {
        return userSession
            .getDataProvider()
            .ConsumerType
            .getConsumerType(idConsumerType)
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(ConsumerTypeHelper.LOG_PREFIX, `Unable to load ConsumerType for id ${idConsumerType}`, error)

                return null
            })
    }

    public static getMainGridColumns(): ColumnModel[] {
      
        const nameColumn: ColumnModel = new ColumnModel()
        const idColumn: ColumnModel = new ColumnModel()
        const consumerCount: ColumnModel = new ColumnModel()

        nameColumn.idName = ConsumerTypeHelper.NAME_DV_KEY.toLowerCase()
        nameColumn.name = ConsumerTypeHelper.NAME_DV_KEY
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
        nameColumn.type = Enums.DataTypeClass.Text

        idColumn.idName = ConsumerTypeHelper.ID_DISTRIBUTOR_DV_KEY.toLowerCase()
        idColumn.name = ConsumerTypeHelper.ID_DISTRIBUTOR_DV_KEY
        idColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Distributor)
        idColumn.type = Enums.DataTypeClass.Integer

        consumerCount.idName = ConsumerTypeHelper.CONSUMER_COUNT_DV_KEY.toLowerCase()
        consumerCount.name = ConsumerTypeHelper.CONSUMER_COUNT_DV_KEY
        consumerCount.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConsumerCount)
        consumerCount.type = Enums.DataTypeClass.Integer

        return [nameColumn, idColumn, consumerCount]
    }

    public static getCreateEditOptions(userSession: IUserSession, idConsumerType: number = 0, consumerType: OpConsumerType = null): CreateEditInitParams {

        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: ConsumerTypeHelper.REFERENCE_TYPE,
            moduleName: ConsumerTypeHelper.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject: ConsumerTypeHelper.createIOpObjectFromOpConsumerType(consumerType),
            referenceObjectId: idConsumerType,

            customDataTypes: {
                paramsRequired: [Enums.DataType.HELPER_NAME, Enums.DataType.HELPER_ID_CONSUMER],
                paramsToEdit: [Enums.DataType.HELPER_NAME, Enums.DataType.HELPER_ID_DISTRIBUTOR]
            }
        }
    }

    public static getDetailsOptions(consumerTypeId: number, pageItemsIds: number[]): DetailsOptions {
        return {
            hasPermission: true,
            idReferenceType: ConsumerTypeHelper.REFERENCE_TYPE,
            moduleName: ConsumerTypeHelper.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: consumerTypeId,
        }
    }

    public static deleteConsumerType(userSession: IUserSession, consumerType: OpConsumerType): JQueryPromise<void> {
        const toDelete: OpConsumerType = new OpConsumerType(null)

        toDelete.idConsumerType = consumerType.idConsumerType
        toDelete.opState = Enums.OpChangeState.Delete

        return userSession
            .getDataProvider()
            .ConsumerType
            .deleteConsumerType(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, ConsumerTypeHelper.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, ConsumerTypeHelper.SIMAX_MODULE_NAME))
                LogHelper.warn(ConsumerTypeHelper.LOG_PREFIX, `Unable to delete consumer type for id ${consumerType.idConsumerType}.`, error)
            })
    }

    public static createIOpObjectFromOpConsumerType(consumerType: OpConsumerType): IOpObject & IReferenceType {
        if (consumerType == null) {
            return null
        }

        const nameDynamicValue: { Key: string, Value: any } = _.find(consumerType.dynamicValues, ((dynamicValue: { Key: string, Value: any }) => dynamicValue.Key === ConsumerTypeHelper.NAME_DV_KEY))
        const idDistributor:number = consumerType.idDistributor

        const dataList: OpDataList<IOpData> = new OpDataList<IOpData>()

        const nameData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_NAME,
            INDEX_NBR: 0,
            VALUE: nameDynamicValue.Value,
            OpState: Enums.OpChangeState.Loaded
        })

        const idDistributorData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_ID_DISTRIBUTOR,
            INDEX_NBR: 0,
            VALUE: idDistributor,
            OpState: Enums.OpChangeState.Loaded
        })

        dataList.push(nameData, idDistributorData)

        return _.assign(consumerType, {
            customFormatString: null,
            idObject: consumerType.idConsumerType,
            dataList,
        })
    }

    public static getParmetersFields(consumerType: OpConsumerType): FieldModel[] {
        const nameFieldModel: FieldModel = new FieldModel()
        const idDistrubutorFieldModel: FieldModel = new FieldModel()
        const consumerCountFieldModel: FieldModel = new FieldModel()

        const nameDynamicValue: { Key: string, Value: any } = _.find(consumerType.dynamicValues, ((dynamicValue: { Key: string, Value: any }) => dynamicValue.Key === ConsumerTypeHelper.NAME_DV_KEY))
        const idDistrubutorDynamicValue: { Key: string, Value: any } = _.find(consumerType.dynamicValues, ((dynamicValue: { Key: string, Value: any }) => dynamicValue.Key === ConsumerTypeHelper.ID_DISTRIBUTOR_DV_KEY))
        const consumerCountDynamicValue: { Key: string, Value: any } = _.find(consumerType.dynamicValues, ((dynamicValue: { Key: string, Value: any }) => dynamicValue.Key === ConsumerTypeHelper.CONSUMER_COUNT_DV_KEY))

        nameFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
        nameFieldModel.typeId = Enums.DataTypeClass.Text

        idDistrubutorFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Distributor)
        idDistrubutorFieldModel.typeId = Enums.DataTypeClass.Text

        consumerCountFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Consumer)
        consumerCountFieldModel.typeId = Enums.DataTypeClass.Text

        if (nameDynamicValue) {
            nameFieldModel.value = nameDynamicValue.Value
        }

        if (idDistrubutorDynamicValue) {
            idDistrubutorFieldModel.value = idDistrubutorDynamicValue.Value
        }

        if (consumerCountDynamicValue) {
            consumerCountFieldModel.value = consumerCountDynamicValue.Value
        }

        return [nameFieldModel, idDistrubutorFieldModel, consumerCountFieldModel]
    }

    public static saveConsumerType(userSession: IUserSession, consumerType: OpConsumerType): JQueryPromise<OpConsumerType> {
        return userSession
            .getDataProvider().ConsumerType
            .saveConsumerType(consumerType)
            .then((savedObject: OpConsumerType) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, ConsumerTypeHelper.SIMAX_MODULE_NAME))

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, ConsumerTypeHelper.SIMAX_MODULE_NAME))
                LogHelper.warn(ConsumerTypeHelper.LOG_PREFIX, `Unable to save consumer type for id ${consumerType.idConsumerType}.`, error)
            })

    }




    // #endregion
}